import { ComponentProps } from 'react'
import { MenuItem } from './types'
import {
  ContextMenu as ContextMenuRoot,
  ContextMenuTrigger,
  ContextMenuContent as ContextMenuContentBase,
  ContextMenuItem,
  ContextMenuCheckboxItem,
  ContextMenuRadioItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuRadioGroup,
} from '@bpinternal/ui-kit-next'
import { trackEvent } from '~/providers'

type ContextMenuProps = Pick<ComponentProps<typeof ContextMenuRoot>, 'onOpenChange' | 'dir' | 'modal'> &
  Pick<ComponentProps<typeof ContextMenuTrigger>, 'disabled' | 'children'> &
  Omit<ComponentProps<typeof ContextMenuContentBase>, 'children' | 'content'> & {
    content: MenuItem[]
  }

export const ContextMenu = ({ children, disabled, onOpenChange, dir, modal, content, ...props }: ContextMenuProps) => {
  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    trackEvent({ type: 'context_menu_opened', content })
  }

  return (
    <ContextMenuRoot {...{ onOpenChange: handleOpenChange, dir, modal }}>
      <ContextMenuTrigger disabled={disabled || !content.filter((t) => !t.hidden).length}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContentBase {...props}>
        <ContextMenuContent content={content} />
      </ContextMenuContentBase>
    </ContextMenuRoot>
  )
}

const ContextMenuContent = ({ content }: { content: MenuItem[] }) => {
  return (
    <>
      {content.map((item, i) => {
        if (item.hidden) {
          return null
        }
        switch (item.type) {
          case 'item': {
            const { content: itemContent, leadingIcon, trailingIcon, ...itemProps } = item
            return (
              <ContextMenuItem key={i} {...itemProps}>
                {leadingIcon}
                <span className="flex-1">{itemContent}</span>
                {trailingIcon}
              </ContextMenuItem>
            )
          }
          case 'separator':
            return <ContextMenuSeparator key={i} />
          case 'label':
            return <ContextMenuLabel key={i}>{item.label}</ContextMenuLabel>
          case 'radioGroup':
            return (
              <ContextMenuRadioGroup key={i} value={item.value}>
                {item.items.map((radioItem, j) => {
                  const { content: itemContent, leadingIcon, trailingIcon, ...itemProps } = radioItem
                  return (
                    <ContextMenuRadioItem key={j} {...itemProps}>
                      {leadingIcon && <span className="absolute left-2">{leadingIcon}</span>}
                      <span className="flex-1">{itemContent}</span>
                      {trailingIcon}
                    </ContextMenuRadioItem>
                  )
                })}
              </ContextMenuRadioGroup>
            )
          case 'submenu':
            return (
              <ContextMenuSub key={i}>
                <ContextMenuSubTrigger disabled={item.disabled}>
                  <div className="flex items-center gap-2">
                    {item.leadingIcon}
                    <span>{item.content}</span>
                  </div>
                </ContextMenuSubTrigger>
                <ContextMenuSubContent>
                  <ContextMenuContent content={item.items} />
                </ContextMenuSubContent>
              </ContextMenuSub>
            )
          case 'checkBoxItem': {
            const { content: itemContent, leadingIcon, trailingIcon, showCheckmark, ...itemProps } = item
            return (
              <ContextMenuCheckboxItem key={i} {...itemProps}>
                {leadingIcon && <span className="absolute left-2">{leadingIcon}</span>}
                <span className="flex-1">{itemContent}</span>
                {trailingIcon}
              </ContextMenuCheckboxItem>
            )
          }
          default:
            return <></>
        }
      })}
    </>
  )
}
